import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState, memo, useRef } from "react";
import Measure from "react-measure";
import { fabric } from "fabric";
import OBCSpinner from "./util/OBC";
import { useSelector } from "react-redux";
import { getRawApiImage } from "./RawImageComponent";
import { generateZoneGrids, generateZoomableImageBbox, generateZoomableImageBboxLabel, getColorFromPercentage, setBboxColor } from "./util/FabricUtils";

import { processTroughingBbox } from "./util/PlaylistUtils";

const EMPTY_FILTERS = {};
const EMPTY_OVERLAY_BOXES = [];
export const STROKE_WIDTH = 1;

const currentDashboardSelector = (state) => _.find(state.dashboards, (dashboard) => dashboard.access_token === state.access_token);
const observationDetectionsSelector = (state) => state.observationDetections;
const markerSelector = (state) => state.markers;

const ZoomableImage = memo(
    ({ imgSrc, zoom, updateZoom, overlayBoxes = EMPTY_OVERLAY_BOXES, filters = EMPTY_FILTERS, withSpinner = false, frame, videoKey, showOverlayContent }) => {
        const [dimensions, setDimensions] = useState({});
        const [fabricCanvasRef, setFabricCanvasRef] = useState(null);
        const [fabricCanvas, setFabricCanvas] = useState(null);
        const [newHeight, setNewHeight] = useState(null);
        const fabricCanvasObjects = useRef([]);

        const [ratioChangeForZoom, setRatioChangeForZoom] = useState(null);
        const [loadingImage, setLoadingImage] = useState(true);
        const [imageObj, setImgObj] = useState(null);
        const [formattedImageSource, setFormattedImageSource] = useState();
        const [imageCacheTimestamp, setImageCacheTimestamp] = useState();

        const currentDashboard = useSelector(currentDashboardSelector);
        const observationDetections = useSelector(observationDetectionsSelector);
        const markers = useSelector(markerSelector);

        useEffect(() => {
            if (fabricCanvas) {
                if (!showOverlayContent) {
                    fabricCanvas.remove(...fabricCanvasObjects.current);
                    return;
                }

                const markerToDisplay = _.find(markers, (marker) => marker.video_key === videoKey && marker.frame === frame);
                const selectedMarker = _.get(markers, "selectedMarker") || {};
                const detectionsToDisplay = observationDetections.filter(
                    (detection) => detection.video_key === videoKey && detection.frame === frame && detection.detection_class === selectedMarker.class,
                );

                fabricCanvas.remove(...fabricCanvasObjects.current);
                fabricCanvasObjects.current = [];

                const bboxOverlay = [];
                const encroachmentBboxes = [];
                const zoneBboxes = [];
                const perspectivePoints = [];

                let leftMostPoint = null;
                let rightMostPoint = null;

                _.forEach(detectionsToDisplay, (detection) => {
                    if (_.get(detection, "detection_class") === "vegetation_encroachment") {
                        const encroachment_bbox = JSON.parse(detection.encroachment_bbox);
                        const zone_bbox = JSON.parse(detection.zone_bbox);
                        const cell_map_data = detection.cell_map_data;
                        const percentage = detection.encroachment_percentage;
                        const zoneSide = detection.zone;
                        encroachmentBboxes.push({ bbox: encroachment_bbox, percentage: percentage, zoneSide: zoneSide });
                        zoneBboxes.push({ bbox: zone_bbox, percentage: percentage, cell_map_data: cell_map_data, zoneSide: zoneSide });

                        const bottomLeft = [zone_bbox[0], zone_bbox[1] + zone_bbox[3]];
                        const bottomRight = [zone_bbox[0] + zone_bbox[2], zone_bbox[1] + zone_bbox[3]];

                        if (!leftMostPoint || bottomLeft[0] < leftMostPoint[0]) {
                            leftMostPoint = bottomLeft;
                        }

                        if (!rightMostPoint || bottomRight[0] > rightMostPoint[0]) {
                            rightMostPoint = bottomRight;
                        }
                    } else {
                        const bbox = JSON.parse(detection.bbox);
                        bboxOverlay.push(processTroughingBbox(bbox, selectedMarker));
                    }
                });

                perspectivePoints.push(leftMostPoint, rightMostPoint);

                // If we have encroachment data, render it on the canvas
                if (encroachmentBboxes.length > 0 && zoneBboxes.length > 0) {
                    // fabric canvas group that will hold all the active cells (i.e. cells with encroachment). This group is then masked
                    // off using the clip path generated from the encroachment bbox to ensure no pixels are drawn outside the encroachment bbox
                    let activeCellGroup = new fabric.Group([], { selectable: false });
                    let cellMapDataPresent = false;

                    zoneBboxes.forEach((zoneBbox) => {
                        // For each zone bbox (up to three per frame - left, four-foot, and right), generate label color and bboxes that will
                        // be used for the rest of the process
                        const labelColor = getColorFromPercentage(zoneBbox.percentage);
                        const newZoneBbox = generateZoomableImageBbox(zoneBbox.bbox, ratioChangeForZoom, "rgba(255, 255, 255, 0.09)");
                        fabricCanvasObjects.current.push(newZoneBbox);

                        // If we have a percentage in the response, generate a label for the percentage, and color it based on the percentage
                        if (zoneBbox.percentage) {
                            const label = `${Math.ceil(zoneBbox.percentage)}%`;
                            const side = zoneBbox.zoneSide;
                            const textBox = generateZoomableImageBboxLabel(zoneBbox.bbox, ratioChangeForZoom, label, labelColor, newHeight, 1, side);
                            fabricCanvasObjects.current.push(textBox);
                        }

                        if (zoneBbox.cell_map_data && zoneBbox.cell_map_data.num_rows && zoneBbox.cell_map_data.num_cols) {
                            cellMapDataPresent = true;
                            // Generate the grid for the zone bbox and the cells with encroachment. First get the grid dimensions and the cell states.
                            // The cell states are an array of 1s and 0s, where 1 indicates an active cell (i.e. encroachment) and 0 indicates an inactive cell
                            const gridRows = zoneBbox.cell_map_data.num_rows;
                            const gridCols = zoneBbox.cell_map_data.num_cols;
                            const cellStates = zoneBbox.cell_map_data.cell_map;

                            // Render the grid
                            generateZoneGrids(gridRows, gridCols, newZoneBbox, fabricCanvas, zoneBbox.percentage, activeCellGroup, cellStates);
                        } else {
                            // If we don't have cell map data, just add the zone bbox to the canvas
                            fabricCanvasObjects.current.push(newZoneBbox);
                        }
                    });

                    encroachmentBboxes.forEach((encroachmentBbox) => {
                        if (!cellMapDataPresent) {
                            // If we don't have cell map data, generate the bbox and color it based on the percentage
                            const color = getColorFromPercentage(encroachmentBbox.percentage);
                            const newEncroachmentBbox = generateZoomableImageBbox(encroachmentBbox.bbox, ratioChangeForZoom, color);
                            fabricCanvasObjects.current.push(newEncroachmentBbox);
                            // fabricCanvas.add(newEncroachmentBbox);
                        } else {
                            // Note: turned off for now, may be used in the future
                            // If we do have cell map data, generate the clip path for the active cells
                            // const [left, top, width, height] = encroachmentBbox.bbox;
                            // const clipPath = new fabric.Rect({
                            //     left: left * ratioChange,
                            //     top: top * ratioChange,
                            //     width: width * ratioChange,
                            //     height: height * ratioChange,
                            //     absolutePositioned: true,
                            // });

                            // Apply the clip path to the active cell group and add it to the canvas
                            // activeCellGroup.clipPath = clipPath;
                            fabricCanvasObjects.current.push(activeCellGroup);
                        }
                    });

                    if (leftMostPoint && rightMostPoint && perspectivePoints.length > 0) {
                        perspectivePoints.forEach((point, index) => {
                            const startX = point[0] * ratioChangeForZoom;
                            const startY = point[1] * ratioChangeForZoom;
                            const endX = startX + (index === 0 ? -70 : 10);
                            const endY = startY + 100;

                            const diagonalLine = new fabric.Line([startX, startY, endX, endY], {
                                strokeWidth: 2,
                                selectable: false,
                                stroke: "rgba(255, 255, 255, 0.2)",
                            });

                            fabricCanvasObjects.current.push(diagonalLine);
                        });

                        const startX = leftMostPoint[0] * ratioChangeForZoom - 100;
                        const startY = leftMostPoint[1] * ratioChangeForZoom;
                        const endX = rightMostPoint[0] * ratioChangeForZoom + 100;
                        const endY = rightMostPoint[1] * ratioChangeForZoom;

                        const verticalLine = new fabric.Line([startX, startY, endX, endY], {
                            strokeWidth: 2,
                            selectable: false,
                            stroke: "rgba(255, 255, 255, 0.2)",
                        });

                        fabricCanvasObjects.current.push(verticalLine);
                    }
                } else if (bboxOverlay.length > 0) {
                    bboxOverlay.forEach((bbox) => {
                        const [left, top, width, height] = bbox;
                        const bboxColor = "red";
                        const box = new fabric.Rect({
                            left: left * ratioChangeForZoom,
                            top: top * ratioChangeForZoom,
                            width: width * ratioChangeForZoom,
                            height: height * ratioChangeForZoom,
                            fill: "",
                            strokeWidth: STROKE_WIDTH,
                            centeredScaling: false,
                            stroke: bboxColor,
                            controls: {
                                ...fabric.Text.prototype.controls,
                                mtr: new fabric.Control({ visible: false }),
                            },
                            selectable: false,
                            hoverCursor: "all-scroll",
                            strokeUniform: true,
                        });

                        fabricCanvasObjects.current.push(box);
                    });
                } else if (markerToDisplay && markerToDisplay.bbox) {
                    const bbox = JSON.parse(selectedMarker.bbox);

                    let overlayTag = "";
                    const bboxColor = setBboxColor(selectedMarker);

                    if (bbox) {
                        const newEncroachmentBbox = generateZoomableImageBbox(bbox, ratioChangeForZoom, bboxColor);
                        fabricCanvasObjects.current.push(newEncroachmentBbox);
                    }

                    const tag = _.get(selectedMarker, "class")
                        .replace(/\bobservations\b/gi, "")
                        .trim();
                    const userCondition = _.get(selectedMarker, "classifications.troughing_condition.user", null);

                    // If user has overridden auto condition then not displaying confidence level
                    if (!userCondition) {
                        const autoCondition = _.get(selectedMarker, "classifications.troughing_condition.auto");
                        const trustLevel = _.get(selectedMarker, "auto_data_full", {});
                        overlayTag = `${tag}${autoCondition ? ` - ${autoCondition}` : ""}${trustLevel.hasOwnProperty(autoCondition) ? `: ${Math.floor(trustLevel[autoCondition] * 100)}%` : ""}`;
                    } else {
                        overlayTag = `${tag}${userCondition ? ` - ${userCondition}` : ""}`;
                    }

                    if (tag && bbox) {
                        const label = generateZoomableImageBboxLabel(bbox, ratioChangeForZoom, overlayTag, bboxColor, newHeight, 1);
                        fabricCanvasObjects.current.push(label);
                    }
                }

                fabricCanvas.add(...fabricCanvasObjects.current);
                fabricCanvas.renderAll();
            }
        }, [observationDetections, frame, videoKey, ratioChangeForZoom, markers, newHeight, fabricCanvas, showOverlayContent, loadingImage]);

        useEffect(() => {
            if (fabricCanvasRef) {
                const dpi = window.devicePixelRatio || 1;

                const canvasElement = fabricCanvasRef;
                const width = canvasElement.clientWidth;
                const height = canvasElement.clientHeight;

                canvasElement.width = width * dpi;
                canvasElement.height = height * dpi;

                const canvas = new fabric.Canvas(canvasElement, {
                    defaultCursor: "default",
                    uniformScaling: false,
                    selection: false,
                    containerClass: "ZoomableImageCanvasContainer",
                });

                setFabricCanvas(canvas);
            }
        }, [fabricCanvasRef]);

        const limitCanvasPosition = useCallback((canvas) => {
            const canvasViewPort = canvas.viewportTransform;

            const bottomEndPoint = canvas.height * (canvasViewPort[0] - 1);
            if (canvasViewPort[5] >= 0 || -bottomEndPoint > canvasViewPort[5]) {
                canvasViewPort[5] = canvasViewPort[5] >= 0 ? 0 : -bottomEndPoint;
            }

            const rightEndPoint = canvas.width * (canvasViewPort[0] - 1);
            if (canvasViewPort[4] >= 0 || -rightEndPoint > canvasViewPort[4]) {
                canvasViewPort[4] = canvasViewPort[4] >= 0 ? 0 : -rightEndPoint;
            }
            canvas.viewportTransform = canvasViewPort;
        }, []);

        const onMove = useCallback(
            (opt, canvas) => {
                if (canvas.isDragging) {
                    const delta = new fabric.Point(opt.e.movementX, opt.e.movementY);
                    canvas.relativePan(delta);
                    limitCanvasPosition(canvas);
                }
            },
            [limitCanvasPosition],
        );

        const onScroll = useCallback(
            (opt) => {
                let zoom = fabricCanvas.getZoom();
                if (opt) {
                    const delta = opt.e.deltaY;
                    zoom *= 0.999 ** delta;
                    zoom = Math.max(Math.min(20, zoom), 1);
                    fabricCanvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
                    opt.e.preventDefault();
                    opt.e.stopPropagation();
                }
                limitCanvasPosition(fabricCanvas);
                if (updateZoom) {
                    updateZoom(zoom);
                }
            },
            [fabricCanvas, limitCanvasPosition],
        );

        useEffect(() => {
            if (fabricCanvas) {
                fabricCanvas.on("mouse:down", function (_) {
                    this.isDragging = true;
                });

                fabricCanvas.on("mouse:move", function (opt) {
                    onMove(opt, this);
                });

                fabricCanvas.on("mouse:up", function (_) {
                    this.setViewportTransform(this.viewportTransform);
                    this.isDragging = false;
                });

                fabricCanvas.on("mouse:wheel", function (opt) {
                    onScroll(opt);
                });

                return () => {
                    fabricCanvas.__eventListeners["mouse:move"] = [];
                    fabricCanvas.__eventListeners["mouse:down"] = [];
                    fabricCanvas.__eventListeners["mouse:up"] = [];
                    fabricCanvas.__eventListeners["mouse:wheel"] = [];
                };
            }
        }, [fabricCanvas, onMove, onScroll]);

        const imageFilters = useMemo(() => {
            const imageFilters = [];
            if (!_.isNil(filters.brightness)) {
                imageFilters.push(new fabric.Image.filters.Brightness({ brightness: filters.brightness }));
            }
            if (!_.isNil(filters.contrast)) {
                imageFilters.push(new fabric.Image.filters.Contrast({ contrast: filters.contrast }));
            }
            return imageFilters;
        }, [filters]);

        useEffect(() => {
            if (imageObj && (imageObj.naturalWidth || imageObj.width)) {
                imageObj.filters = imageFilters;
                imageObj.applyFilters();
                fabricCanvas.renderAll();
            }
        }, [imageObj, imageFilters, fabricCanvas]);

        useEffect(() => {
            if (imgSrc && currentDashboard) {
                setLoadingImage(true);
                getRawApiImage(imgSrc, currentDashboard.access_token, (imageSrc) => {
                    setFormattedImageSource(imageSrc);
                    setImageCacheTimestamp(Date.now());
                });
            } else if (imgSrc) {
                setFormattedImageSource(imgSrc);
            }
        }, [currentDashboard, imgSrc]);

        useEffect(() => {
            if (fabricCanvas) {
                fabric.Image.fromURL(
                    formattedImageSource,
                    function (img) {
                        if (imageObj) {
                            fabricCanvas.remove(imageObj);
                        }
                        const widthRatioChange = dimensions.width / img.width;
                        const heightRatioChange = dimensions.height / img.height;

                        let ratioChange;
                        if (img.width * heightRatioChange > dimensions.width) {
                            ratioChange = widthRatioChange;
                            setRatioChangeForZoom(widthRatioChange);
                        } else {
                            ratioChange = heightRatioChange;
                            setRatioChangeForZoom(heightRatioChange);
                        }

                        const newWidth = img.width * ratioChange;
                        const newHeight = img.height * ratioChange;

                        setNewHeight(newHeight);

                        fabricCanvas.setWidth(newWidth);
                        fabricCanvas.setHeight(newHeight);

                        const imageData = {
                            left: 0,
                            top: 0,
                            controls: {},
                            hasBorders: false,
                            selectable: false,
                            scaleY: ratioChange,
                            scaleX: ratioChange,
                        };

                        const oImg = img.set(imageData);
                        fabricCanvas.add(oImg);
                        setImgObj(oImg);
                        setLoadingImage(false);
                    },
                    { crossOrigin: "anonymous" },
                );
            }
        }, [dimensions.height, dimensions.width, fabricCanvas, formattedImageSource, imageCacheTimestamp]);

        useEffect(() => {
            if (fabricCanvas && imageObj) {
                const currentZoom = fabricCanvas.getZoom();
                if (currentZoom !== zoom) {
                    let x = fabricCanvas.width / 2;
                    let y = fabricCanvas.height / 2;

                    if (overlayBoxes && overlayBoxes[0] && ratioChangeForZoom) {
                        const [left, top, width, height] = overlayBoxes[0];
                        let leftOffset = 0;
                        let topOffset = 0;
                        if (left * ratioChangeForZoom < fabricCanvas.width / 2) {
                            leftOffset = width * ratioChangeForZoom;
                        }
                        if (top * ratioChangeForZoom < fabricCanvas.height / 2) {
                            topOffset = height * ratioChangeForZoom;
                        }
                        x = left * ratioChangeForZoom + (width * ratioChangeForZoom - leftOffset);
                        y = top * ratioChangeForZoom + (height * ratioChangeForZoom - topOffset);
                    }

                    fabricCanvas.zoomToPoint(new fabric.Point(x, y), zoom);
                    limitCanvasPosition(fabricCanvas);
                    fabricCanvas.renderAll();
                }
            }
        }, [fabricCanvas, imageObj, zoom, ratioChangeForZoom, overlayBoxes, limitCanvasPosition]);

        return (
            <>
                {loadingImage && withSpinner && (
                    <div
                        className="SpinnerContainer"
                        style={{ position: "absolute", zIndex: 2000 }}>
                        <OBCSpinner
                            size={80}
                            speed={3}
                            colorScheme={"mono"}
                        />
                    </div>
                )}
                <Measure
                    bounds
                    onResize={(contentRect) => {
                        setDimensions(contentRect.bounds);
                    }}>
                    {({ measureRef }) => {
                        return (
                            <div
                                className="ZoomableImage"
                                ref={measureRef}>
                                <canvas
                                    ref={(c) => setFabricCanvasRef(c)}
                                    className="ZoomFabricCanvas"
                                />
                            </div>
                        );
                    }}
                </Measure>
            </>
        );
    },
);

export default ZoomableImage;
